// Hard gates stay out of the average. SpineLevel.orient is never read; flags keep the scorer pure.
import { countedWeightPounds } from "@CommonUtils/pog/productWeight";
import {
  resolveAestheticsRules,
  type AestheticsRules,
  type AestheticsWeights,
} from "./aestheticsRules";
import {
  PATH_SEPARATOR,
  effectiveAttrs,
  type Flag,
  type StudioDraft,
  type StudioItem,
  type StudioRunGeometry,
} from "./studioTypes";

export interface AestheticsInput {
  items: readonly StudioItem[];
  runs: readonly StudioRunGeometry[];
  draft: StudioDraft;
  /** Omitted, the detector half stands down and CDT reports the ordering half alone. */
  flags?: readonly Flag[];
  /** House rules; anything omitted is filled positionally from the spine. */
  rules?: Partial<AestheticsRules>;
  /** Omitted, the range gates report `evaluated: false`. */
  rangedSkuIds?: readonly string[];
}

export interface SubScore {
  /** 0–1. */
  score: number;
  detail: Readonly<Record<string, number>>;
}

export interface GateResult {
  id: string;
  label: string;
  /** false when the gate could not be evaluated (see `evaluated`). */
  passed: boolean;
  evaluated: boolean;
  count: number;
  examples: readonly string[];
}

export interface WorstBlock {
  /** Block path, e.g. "Detergent › Tide › Liquid". */
  block: string;
  /** (max start − min start) + (max end − min end), in cm. */
  raggedCm: number;
  /** The block's widest pack: the unit raggedness is read against. */
  widestPackCm: number;
  shelves: readonly number[];
  footprintCm: number;
  score: number;
}

export interface EmptiestShelf {
  run: number;
  shelf: number;
  largestGapCm: number;
  gapAtXCm: number;
  totalAirCm: number;
}

export interface OrderingBreak {
  /** Which ordering rule broke: size / tier / last-in-block / flavour ribbon. */
  rule: string;
  block: string;
  detail: string;
  violations: number;
}

export interface AestheticsScore {
  /** 0–100, rounded. */
  score: number;
  weights: AestheticsWeights;
  blocking: SubScore & { vertical: SubScore; horizontal: SubScore };
  cdt: SubScore & { detector: SubScore | null; ordering: SubScore };
  fullness: SubScore;
  ergonomics: SubScore;
  gates: readonly GateResult[];
  /** True only when every gate that could be evaluated passed. */
  gatesPassed: boolean;
  worst: {
    blocks: readonly WorstBlock[];
    emptiestShelf: EmptiestShelf | null;
    ordering: OrderingBreak | null;
  };
  rules: AestheticsRules;
}

interface Stretch {
  item: StudioItem;
  attrs: Record<string, string | null>;
  run: number;
  shelf: number;
  x: number;
  width: number;
  hFacings: number;
  packWidth: number;
}

const EPS = 0.25;

const clamp01 = (value: number) => (value < 0 ? 0 : value > 1 ? 1 : value);

const laneKey = (run: number, shelf: number) => `${run}|${shelf}`;

const toStretches = (items: readonly StudioItem[], draft: StudioDraft): Stretch[] => {
  const out: Stretch[] = [];
  for (const item of items) {
    const attrs = effectiveAttrs(item, draft);
    for (const placement of item.placements) {
      if (!(placement.widthCm > 0)) continue;
      out.push({
        item,
        attrs,
        run: placement.run,
        shelf: placement.shelf,
        x: placement.xCm,
        width: placement.widthCm,
        hFacings: placement.hFacings,
        packWidth:
          item.facingWidthCm > 0
            ? item.facingWidthCm
            : placement.widthCm / Math.max(1, placement.hFacings),
      });
    }
  }
  return out;
};

const groupBy = <T>(list: readonly T[], key: (entry: T) => string): Map<string, T[]> => {
  const map = new Map<string, T[]>();
  for (const entry of list) {
    const k = key(entry);
    const bucket = map.get(k);
    if (bucket) bucket.push(entry);
    else map.set(k, [entry]);
  }
  return map;
};

/** Lanes in reading order: left to right within a shelf. */
const lanesOf = (stretches: readonly Stretch[]): Map<string, Stretch[]> => {
  const lanes = groupBy(stretches, (s) => laneKey(s.run, s.shelf));
  lanes.forEach((list) => list.sort((a, b) => a.x - b.x));
  return lanes;
};

/** Magnitude in a comparable unit plus its family; only same-family values compare. */
const sizeMagnitude = (label: string | null): { family: string; magnitude: number } | null => {
  if (!label) return null;
  const number = parseFloat(label.match(/-?\d+(?:\.\d+)?/)?.[0] ?? "");
  if (!Number.isFinite(number)) return null;
  if (/fl\s*oz/i.test(label)) return { family: "volume", magnitude: number };
  if (/\bml\b/i.test(label)) return { family: "volume", magnitude: number * 0.033814 };
  if (/\bl\b|liter|litre/i.test(label)) return { family: "volume", magnitude: number * 33.814 };
  if (/\blb\b|pound/i.test(label)) return { family: "mass", magnitude: number * 16 };
  if (/\boz\b|ounce/i.test(label)) return { family: "mass", magnitude: number };
  if (/\bct\b|count|\bpk\b|pack|pac|pod/i.test(label))
    return { family: "count", magnitude: number };
  if (/load/i.test(label)) return { family: "loads", magnitude: number };
  return { family: "bare", magnitude: number };
};

/** Raggedness vs widest pack: one pack per edge is forgiven (blocks end on pack boundaries). */
const scoreVerticalBlocking = (
  stretches: readonly Stretch[],
  rules: AestheticsRules,
  bayWidthCm: number,
): { sub: SubScore; worst: WorstBlock[] } => {
  const blocks: WorstBlock[] = [];
  let weighted = 0;
  let weight = 0;
  let raggedTotal = 0;

  for (let depth = 0; depth < rules.verticalAttrs.length; depth++) {
    const path = rules.verticalAttrs.slice(0, depth + 1);
    const grouped = groupBy(stretches, (s) =>
      path.map((attr) => s.attrs[attr] ?? "—").join(PATH_SEPARATOR),
    );
    grouped.forEach((members, key) => {
      const byLane = groupBy(members, (s) => laneKey(s.run, s.shelf));
      // A block confined to one shelf has no vertical edges to be straight.
      if (byLane.size < 2) return;
      // Per run: a block legitimately restarts its x in the next aisle.
      const byRun = groupBy([...byLane.entries()], ([lane]) => lane.split("|")[0]);
      const widest = members.reduce((max, s) => Math.max(max, s.packWidth), 0) || 1;
      const isBayOwner = rules.bayOwners.some((owner) =>
        key.toLowerCase().includes(owner.toLowerCase()),
      );
      byRun.forEach((laneEntries) => {
        if (laneEntries.length < 2) return;
        const starts: number[] = [];
        const ends: number[] = [];
        const shelves: number[] = [];
        let footprint = 0;
        for (const [lane, list] of laneEntries) {
          starts.push(Math.min(...list.map((s) => s.x)));
          ends.push(Math.max(...list.map((s) => s.x + s.width)));
          shelves.push(Number(lane.split("|")[1]));
          footprint += list.reduce((sum, s) => sum + s.width, 0);
        }
        let ragged =
          Math.max(...starts) - Math.min(...starts) + (Math.max(...ends) - Math.min(...ends));
        // A bay owner also owes the distance from its edges to the bay line.
        if (isBayOwner && bayWidthCm > 0) {
          const offBayLine = (edge: number) => {
            const offset = ((edge % bayWidthCm) + bayWidthCm) % bayWidthCm;
            return Math.min(offset, bayWidthCm - offset);
          };
          const median = (values: number[]) =>
            [...values].sort((a, b) => a - b)[Math.floor(values.length / 2)];
          ragged += offBayLine(median(starts)) + offBayLine(median(ends));
        }
        const excess = Math.max(0, ragged - rules.jitterPacks * widest);
        const score = clamp01(1 - excess / (rules.raggedZeroPacks * widest));
        blocks.push({
          block: key,
          raggedCm: +ragged.toFixed(1),
          widestPackCm: +widest.toFixed(1),
          shelves: shelves.sort((a, b) => a - b),
          footprintCm: +footprint.toFixed(1),
          score: +score.toFixed(3),
        });
        weighted += score * footprint;
        weight += footprint;
        raggedTotal += ragged * footprint;
      });
    });
  }

  const worst = [...blocks]
    .sort((a, b) => (1 - a.score) * a.footprintCm - (1 - b.score) * b.footprintCm)
    .reverse()
    .filter((block) => block.score < 1)
    .slice(0, 3);

  return {
    sub: {
      score: weight > 0 ? weighted / weight : 1,
      detail: {
        blocks: blocks.length,
        meanRaggedCm: weight > 0 ? +(raggedTotal / weight).toFixed(1) : 0,
        footprintCm: +weight.toFixed(0),
      },
    },
    worst,
  };
};

/** Index into `tierOrder` (top tier = 0), or null for an untiered pack format. */
const tierIndexer = (rules: AestheticsRules) => {
  const byValue = new Map<string, number>();
  rules.tierOrder.forEach((tier, index) => {
    for (const value of tier.values) byValue.set(value.trim().toLowerCase(), index);
  });
  return (stretch: Stretch): number | null => {
    if (!rules.tierAttr) return null;
    const value = (stretch.attrs[rules.tierAttr] ?? "").trim().toLowerCase();
    return byValue.has(value) ? byValue.get(value)! : null;
  };
};

/** The tier owning the most width, and its share; untiered stretches are excluded. */
const dominantTier = (
  list: readonly Stretch[],
  tierOf: (stretch: Stretch) => number | null,
): { tier: number | null; share: number; width: number } => {
  const widths = new Map<number, number>();
  let total = 0;
  for (const stretch of list) {
    const tier = tierOf(stretch);
    if (tier === null) continue;
    widths.set(tier, (widths.get(tier) ?? 0) + stretch.width);
    total += stretch.width;
  }
  let best: number | null = null;
  let bestWidth = 0;
  widths.forEach((width, tier) => {
    if (width > bestWidth) {
      bestWidth = width;
      best = tier;
    }
  });
  return { tier: best, share: total > 0 ? bestWidth / total : 1, width: total };
};

/** No tier attr/order: stand down (score 1) rather than guess. */
const scoreHorizontalBlocking = (
  stretches: readonly Stretch[],
  rules: AestheticsRules,
): SubScore => {
  if (!rules.tierAttr || rules.tierOrder.length === 0) {
    return { score: 1, detail: { graded: 0 } };
  }
  const tierOf = tierIndexer(rules);

  /* a · aisle-wide purity of each shelf. */
  const lanes = lanesOf(stretches);
  let purityWeighted = 0;
  let purityWeight = 0;
  lanes.forEach((list) => {
    const dominant = dominantTier(list, tierOf);
    purityWeighted += dominant.share * dominant.width;
    purityWeight += dominant.width;
  });
  const aisleTierPurity = purityWeight > 0 ? purityWeighted / purityWeight : 1;

  /* b+c · per column (a vertical brand block), one lane at a time. */
  const columns = groupBy(stretches, (s) => `${s.run}|${s.attrs[rules.brandAttr] ?? "—"}`);
  let boundaryWeighted = 0;
  let boundaryWeight = 0;
  let orderedColumns = 0;
  let gradedColumns = 0;

  columns.forEach((members) => {
    const byShelf = groupBy(members, (s) => String(s.shelf));
    const shelfEntries = [...byShelf.entries()].sort((a, b) => Number(a[0]) - Number(b[0]));
    for (const [, list] of shelfEntries) {
      const dominant = dominantTier(list, tierOf);
      boundaryWeighted += dominant.share * dominant.width;
      boundaryWeight += dominant.width;
    }
    if (shelfEntries.length < 2) return;
    // Clean stacking: tiers never go backwards and none returns after another.
    const reading = shelfEntries
      .map(([, list]) => dominantTier(list, tierOf).tier)
      .filter((tier): tier is number => tier !== null);
    if (reading.length < 2) return;
    gradedColumns++;
    let clean = true;
    const seen = new Set<number>();
    let previous = -1;
    let previousTier = -1;
    for (const tier of reading) {
      if (tier !== previousTier && seen.has(tier)) clean = false;
      if (tier < previous) clean = false;
      previous = Math.max(previous, tier);
      seen.add(tier);
      previousTier = tier;
    }
    if (clean) orderedColumns++;
  });

  const columnBoundary = boundaryWeight > 0 ? boundaryWeighted / boundaryWeight : 1;
  const columnTierOrder = gradedColumns > 0 ? orderedColumns / gradedColumns : 1;

  return {
    score: (aisleTierPurity + columnBoundary + columnTierOrder) / 3,
    detail: {
      aisleTierPurity: +aisleTierPurity.toFixed(3),
      columnBoundary: +columnBoundary.toFixed(3),
      columnTierOrder: +columnTierOrder.toFixed(3),
      graded: gradedColumns,
    },
  };
};

const scoreDetector = (
  flags: readonly Flag[],
  skuCount: number,
  rules: AestheticsRules,
): SubScore => {
  const penalty = flags.reduce((sum, flag) => sum + (rules.severityPenalty[flag.severity] ?? 0), 0);
  const perHundred = skuCount > 0 ? (penalty * 100) / skuCount : 0;
  return {
    score: clamp01(1 - perHundred / rules.flagsPerHundredZero),
    detail: {
      flags: flags.length,
      weightedPenalty: +penalty.toFixed(2),
      perHundredSkus: +perHundred.toFixed(2),
    },
  };
};

/** Ordering consistency: each measure is a fraction of pairs (or blocks) that read correctly. */
const scoreOrdering = (
  stretches: readonly Stretch[],
  rules: AestheticsRules,
): { sub: SubScore; breaks: OrderingBreak[] } => {
  const breaks: OrderingBreak[] = [];
  const lanes = lanesOf(stretches);
  const blockKey = (s: Stretch) =>
    rules.verticalAttrs.map((attr) => s.attrs[attr] ?? "—").join(PATH_SEPARATOR);

  /* a · sizes ascend within a block, along the shelf. */
  let sizePairs = 0;
  let sizeBad = 0;
  const sizeBadByBlock = new Map<string, number>();
  lanes.forEach((list) => {
    for (let i = 1; i < list.length; i++) {
      const a = list[i - 1];
      const b = list[i];
      if (blockKey(a) !== blockKey(b)) continue;
      const left = sizeMagnitude(a.attrs[rules.sizeAttr]);
      const right = sizeMagnitude(b.attrs[rules.sizeAttr]);
      if (!left || !right || left.family !== right.family) continue;
      if (left.magnitude === right.magnitude) continue;
      sizePairs++;
      if (left.magnitude > right.magnitude) {
        sizeBad++;
        sizeBadByBlock.set(blockKey(a), (sizeBadByBlock.get(blockKey(a)) ?? 0) + 1);
      }
    }
  });
  const sizeAscending = sizePairs > 0 ? 1 - sizeBad / sizePairs : 1;
  if (sizeBad > 0) {
    const [block, count] = [...sizeBadByBlock.entries()].sort((a, b) => b[1] - a[1])[0];
    breaks.push({
      rule: "size ascending",
      block,
      detail: `${sizeBad} ${sizeBad === 1 ? "place" : "places"} where a bigger pack comes before a smaller one along the shelf`,
      violations: count,
    });
  }

  /* b · the tier ladder reads in declared order along the shelf, judged in the
     block above the tier level. */
  const tierBlockAttrs = rules.verticalAttrs.slice(
    0,
    Math.max(0, rules.verticalAttrs.indexOf(rules.tierAttr)),
  );
  const tierBlockKey = (s: Stretch) =>
    (tierBlockAttrs.length ? tierBlockAttrs : [rules.brandAttr])
      .map((attr) => s.attrs[attr] ?? "—")
      .join(PATH_SEPARATOR);
  const tierOf = tierIndexer(rules);
  let tierPairs = 0;
  let tierBad = 0;
  const tierBadByBlock = new Map<string, number>();
  if (rules.tierAttr && rules.tierOrder.length > 0) {
    lanes.forEach((list) => {
      for (let i = 1; i < list.length; i++) {
        const a = list[i - 1];
        const b = list[i];
        if (tierBlockKey(a) !== tierBlockKey(b)) continue;
        const left = tierOf(a);
        const right = tierOf(b);
        if (left === null || right === null || left === right) continue;
        tierPairs++;
        if (left > right) {
          tierBad++;
          tierBadByBlock.set(tierBlockKey(a), (tierBadByBlock.get(tierBlockKey(a)) ?? 0) + 1);
        }
      }
    });
  }
  const tierLadder = tierPairs > 0 ? 1 - tierBad / tierPairs : 1;
  if (tierBad > 0) {
    const [block, count] = [...tierBadByBlock.entries()].sort((a, b) => b[1] - a[1])[0];
    breaks.push({
      rule: "handle tier order",
      block,
      detail: `${tierBad} pair(s) put a later handle tier before an earlier one`,
      violations: count,
    });
  }

  /* c · `lastInBlock` values close their block rather than standing inside it. */
  const isLastValue = (stretch: Stretch) => {
    const haystack = [stretch.attrs[rules.flavourAttr], stretch.item.name]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return rules.lastInBlock.some((needle) => haystack.includes(needle.toLowerCase()));
  };
  // Shelves top → bottom, left → right within a shelf.
  const readingIndex = new Map<Stretch, number>();
  {
    const ordered = [...stretches].sort((a, b) => a.run - b.run || a.shelf - b.shelf || a.x - b.x);
    ordered.forEach((stretch, index) => readingIndex.set(stretch, index));
  }
  // "Last in its block" is the right end of the block's stripe on that shelf, over the deepest
  // vertical block rather than the brand.
  const blocksForLast = groupBy(stretches, (s) => `${laneKey(s.run, s.shelf)}|${blockKey(s)}`);
  let lastBlocks = 0;
  let lastOk = 0;
  const earlyByBlock = new Map<string, number>();
  blocksForLast.forEach((members, key) => {
    const marked = members.filter(isLastValue);
    if (!marked.length || marked.length === members.length) return;
    lastBlocks++;
    const rightmostOther = Math.max(
      ...members.filter((s) => !isLastValue(s)).map((s) => s.x + s.width),
    );
    const early = marked.filter((s) => s.x + s.width < rightmostOther - EPS);
    if (early.length === 0) lastOk++;
    else {
      const block = key.split("|").slice(2).join("|");
      earlyByBlock.set(block, (earlyByBlock.get(block) ?? 0) + early.length);
    }
  });
  const sensitiveLast = lastBlocks > 0 ? lastOk / lastBlocks : 1;
  const [worstLastBlock, worstLastCount] = [...earlyByBlock.entries()].sort(
    (a, b) => b[1] - a[1],
  )[0] ?? ["", 0];
  if (worstLastCount > 0) {
    breaks.push({
      rule: "attribute reads last",
      block: worstLastBlock,
      detail: `${worstLastCount} designated end-value facing(s) stand inside the block instead of closing it`,
      violations: worstLastCount,
    });
  }

  /* d · the same flavour ribbon recurs brand to brand. */
  const flavourByBrand = new Map<string, string[]>();
  groupBy(stretches, (s) => String(s.attrs[rules.brandAttr] ?? "—")).forEach((members, brand) => {
    const seen: string[] = [];
    for (const stretch of [...members].sort(
      (a, b) => readingIndex.get(a)! - readingIndex.get(b)!,
    )) {
      const value = stretch.attrs[rules.flavourAttr];
      if (!value) continue;
      if (!seen.includes(value)) seen.push(value);
    }
    if (seen.length >= 2) flavourByBrand.set(brand, seen);
  });
  /* Consensus, not conformance to a reference: per flavour pair the minority reading is the
     disagreement, so any order every brand shares scores 1. */
  const pairVotes = new Map<string, { forward: number; backward: number }>();
  flavourByBrand.forEach((sequence) => {
    for (let i = 0; i < sequence.length; i++) {
      for (let j = i + 1; j < sequence.length; j++) {
        const [a, b] = [sequence[i], sequence[j]];
        const forward = a < b;
        const key = forward ? `${a}␟${b}` : `${b}␟${a}`;
        const votes = pairVotes.get(key) ?? { forward: 0, backward: 0 };
        if (forward) votes.forward++;
        else votes.backward++;
        pairVotes.set(key, votes);
      }
    }
  });
  let ribbonPairs = 0;
  let ribbonDisagreements = 0;
  pairVotes.forEach((votes) => {
    ribbonPairs += votes.forward + votes.backward;
    ribbonDisagreements += Math.min(votes.forward, votes.backward);
  });
  const flavourRibbon = ribbonPairs > 0 ? 1 - ribbonDisagreements / ribbonPairs : 1;
  const ribbonBrands = flavourByBrand.size;
  // Name the brand that disagrees with the aisle most often.
  let worstRibbonBrand = "";
  let worstRibbonCount = 0;
  flavourByBrand.forEach((sequence, brand) => {
    let against = 0;
    for (let i = 0; i < sequence.length; i++) {
      for (let j = i + 1; j < sequence.length; j++) {
        const [a, b] = [sequence[i], sequence[j]];
        const forward = a < b;
        const votes = pairVotes.get(forward ? `${a}␟${b}` : `${b}␟${a}`);
        if (!votes) continue;
        const majorityForward = votes.forward >= votes.backward;
        if (forward !== majorityForward) against++;
      }
    }
    if (against > worstRibbonCount) {
      worstRibbonCount = against;
      worstRibbonBrand = brand;
    }
  });
  if (worstRibbonCount > 0) {
    breaks.push({
      rule: "terminal variant ribbon",
      block: worstRibbonBrand,
      detail: `this brand reads ${worstRibbonCount} terminal-variant pair(s) the opposite way round from the rest of the aisle`,
      violations: worstRibbonCount,
    });
  }

  return {
    sub: {
      score: (sizeAscending + tierLadder + sensitiveLast + flavourRibbon) / 4,
      detail: {
        sizeAscending: +sizeAscending.toFixed(3),
        tierLadder: +tierLadder.toFixed(3),
        sensitiveLast: +sensitiveLast.toFixed(3),
        flavourRibbon: +flavourRibbon.toFixed(3),
        sizePairs,
        tierPairs,
        lastBlocks,
        ribbonBrands,
      },
    },
    breaks,
  };
};

/** Square gaps vs half a bay so one hole costs more than the same air spread thin. */
const scoreFullness = (
  stretches: readonly Stretch[],
  runs: readonly StudioRunGeometry[],
): { sub: SubScore; emptiest: EmptiestShelf | null } => {
  const lanes = lanesOf(stretches);
  let weighted = 0;
  let weight = 0;
  let overhangCm = 0;
  let airTotal = 0;
  let emptiest: EmptiestShelf | null = null;

  // Untouched shelves are lanes too; skipping them would hide the emptiest.
  for (const run of runs) {
    const gapReference = (run.bayWidthCm || 121.92) / 2;
    for (const shelf of run.shelves) {
      const list = lanes.get(laneKey(run.run, shelf.rank)) ?? [];
      const width = run.widthCm;
      const gaps: { size: number; at: number }[] = [];
      let cursor = 0;
      for (const stretch of list) {
        const gap = stretch.x - cursor;
        if (gap > EPS) gaps.push({ size: gap, at: cursor });
        cursor = Math.max(cursor, stretch.x + stretch.width);
        if (stretch.x + stretch.width > width + EPS) {
          overhangCm += stretch.x + stretch.width - width;
        }
        if (stretch.x < -EPS) overhangCm += -stretch.x;
      }
      const tail = width - cursor;
      if (tail > EPS) gaps.push({ size: tail, at: cursor });
      const air = gaps.reduce((sum, gap) => sum + gap.size, 0);
      airTotal += air;
      const penalty = gaps.reduce(
        (sum, gap) => sum + (gap.size * gap.size) / (gapReference * gapReference),
        0,
      );
      const laneScore = clamp01(1 - penalty);
      weighted += laneScore * width;
      weight += width;
      const largest = gaps.reduce((best, gap) => (gap.size > best.size ? gap : best), {
        size: 0,
        at: 0,
      });
      if (!emptiest || largest.size > emptiest.largestGapCm) {
        emptiest = {
          run: run.run,
          shelf: shelf.rank,
          largestGapCm: +largest.size.toFixed(1),
          gapAtXCm: +largest.at.toFixed(1),
          totalAirCm: +air.toFixed(1),
        };
      }
    }
  }

  const totalWidth = runs.reduce((sum, run) => sum + run.widthCm * run.shelves.length, 0) || 1;
  const gapScore = weight > 0 ? weighted / weight : 1;
  // Charged against a bay width: a bay's worth of overhang wipes the component out.
  const overhangPenalty = clamp01(overhangCm / (runs[0]?.bayWidthCm || 121.92));

  return {
    sub: {
      score: clamp01(gapScore - overhangPenalty),
      detail: {
        gapScore: +gapScore.toFixed(3),
        overhangCm: +overhangCm.toFixed(1),
        totalAirCm: +airTotal.toFixed(1),
        airShare: +(airTotal / totalWidth).toFixed(4),
      },
    },
    emptiest,
  };
};

/** Pack weight is derived, not sourced. */
const scoreErgonomics = (
  stretches: readonly Stretch[],
  runs: readonly StudioRunGeometry[],
  rules: AestheticsRules,
): SubScore => {
  if (rules.bulkPounds <= 0) {
    return { score: 1, detail: { bulkFacings: 0, bulkWidthCm: 0, bulkLowShare: 1 } };
  }
  const lowerRanks = new Map<number, Set<number>>();
  for (const run of runs) {
    const ranks = run.shelves.map((shelf) => shelf.rank).sort((a, b) => a - b);
    const lowCount = Math.max(1, Math.round(ranks.length * rules.lowerShelfShare));
    lowerRanks.set(run.run, new Set(ranks.slice(ranks.length - lowCount)));
  }
  let bulkLow = 0;
  let bulkTotal = 0;
  let bulkFacings = 0;
  for (const stretch of stretches) {
    const pounds = countedWeightPounds(
      {
        name: stretch.item.name,
        size: stretch.attrs[rules.sizeAttr] ?? "",
        unitWeight: stretch.item.weightKg,
        weightUom: stretch.item.weightKg === undefined ? null : "kg",
      },
      "UNIT",
    );
    if (pounds === null || pounds < rules.bulkPounds) continue;
    bulkTotal += stretch.width;
    bulkFacings += stretch.hFacings;
    if (lowerRanks.get(stretch.run)?.has(stretch.shelf)) bulkLow += stretch.width;
  }
  return {
    // No bulk on the plan is not a failure; there is nothing to get wrong.
    score: bulkTotal > 0 ? bulkLow / bulkTotal : 1,
    detail: {
      bulkFacings,
      bulkWidthCm: +bulkTotal.toFixed(1),
      bulkLowShare: bulkTotal > 0 ? +(bulkLow / bulkTotal).toFixed(3) : 1,
    },
  };
};

const evaluateGates = (
  input: AestheticsInput,
  stretches: readonly Stretch[],
  rules: AestheticsRules,
): GateResult[] => {
  const { runs, items } = input;
  const lanes = lanesOf(stretches);

  const overlaps: string[] = [];
  lanes.forEach((list) => {
    for (let i = 1; i < list.length; i++) {
      const overlap = list[i - 1].x + list[i - 1].width - list[i].x;
      if (overlap > EPS) {
        overlaps.push(
          `${list[i - 1].item.name} / ${list[i].item.name} overlap ${overlap.toFixed(1)}cm on shelf ${list[i].shelf}`,
        );
      }
    }
  });

  const past: string[] = [];
  const byRun = new Map(runs.map((run) => [run.run, run]));
  for (const stretch of stretches) {
    const run = byRun.get(stretch.run);
    if (!run) continue;
    if (stretch.x < -EPS || stretch.x + stretch.width > run.widthCm + EPS) {
      past.push(
        `${stretch.item.name} runs to ${(stretch.x + stretch.width).toFixed(1)}cm of ${run.widthCm.toFixed(1)}cm on shelf ${stretch.shelf}`,
      );
    }
  }

  const emptyShelves: string[] = [];
  for (const run of runs) {
    for (const shelf of run.shelves) {
      if (!(lanes.get(laneKey(run.run, shelf.rank))?.length ?? 0)) {
        emptyShelves.push(`run ${run.run + 1}, shelf ${shelf.rank} is empty`);
      }
    }
  }

  const placed = new Set(stretches.map((stretch) => stretch.item.skuId));
  const rangeGiven = (input.rangedSkuIds?.length ?? 0) > 0;
  const unplaced = rangeGiven
    ? input.rangedSkuIds!.filter((skuId) => !placed.has(skuId))
    : items.filter((item) => item.placements.length === 0).map((item) => item.skuId);

  // Eliminated: the range knows the brand and the shelf shows none of it.
  const brandOf = new Map<string, string>();
  for (const item of items) {
    brandOf.set(item.skuId, String(effectiveAttrs(item, input.draft)[rules.brandAttr] ?? "—"));
  }
  const placedBrands = new Set(
    stretches.map((stretch) => String(stretch.attrs[rules.brandAttr] ?? "—")),
  );
  const knownBrands = new Set(
    (rangeGiven ? input.rangedSkuIds! : items.map((item) => item.skuId))
      .map((skuId) => brandOf.get(skuId))
      .filter((brand): brand is string => Boolean(brand)),
  );
  const eliminated = [...knownBrands].filter((brand) => !placedBrands.has(brand));

  return [
    {
      id: "overlap",
      label: "No overlapping facings",
      passed: overlaps.length === 0,
      evaluated: true,
      count: overlaps.length,
      examples: overlaps.slice(0, 3),
    },
    {
      id: "past-edge",
      label: "Nothing past the run edge",
      passed: past.length === 0,
      evaluated: true,
      count: past.length,
      examples: past.slice(0, 3),
    },
    {
      id: "empty-shelf",
      label: "No empty shelf",
      passed: emptyShelves.length === 0,
      evaluated: true,
      count: emptyShelves.length,
      examples: emptyShelves.slice(0, 3),
    },
    {
      id: "unplaced",
      label: "Every ranged SKU placed",
      passed: unplaced.length === 0,
      evaluated: rangeGiven || items.some((item) => item.placements.length === 0),
      count: unplaced.length,
      examples: unplaced.slice(0, 3),
    },
    {
      id: "brand-eliminated",
      label: "No brand eliminated",
      passed: eliminated.length === 0,
      evaluated: rangeGiven,
      count: eliminated.length,
      examples: eliminated.slice(0, 3),
    },
  ];
};

export const scorePlanogramAesthetics = (input: AestheticsInput): AestheticsScore => {
  const rules = resolveAestheticsRules(input.draft, input.rules);
  const stretches = toStretches(input.items, input.draft);
  const bayWidthCm = input.runs[0]?.bayWidthCm || 121.92;

  const vertical = scoreVerticalBlocking(stretches, rules, bayWidthCm);
  const horizontal = scoreHorizontalBlocking(stretches, rules);
  const blockingScore =
    vertical.sub.score * rules.blockingSplit.vertical +
    horizontal.score * rules.blockingSplit.horizontal;

  const ordering = scoreOrdering(stretches, rules);
  const skuCount = new Set(stretches.map((stretch) => stretch.item.skuId)).size;
  const detector = input.flags ? scoreDetector(input.flags, skuCount, rules) : null;
  // With no detector run, the ordering half carries the component alone.
  const cdtScore = detector
    ? detector.score * rules.cdtSplit.detector + ordering.sub.score * rules.cdtSplit.ordering
    : ordering.sub.score;

  const fullness = scoreFullness(stretches, input.runs);
  const ergonomics = scoreErgonomics(stretches, input.runs, rules);

  const { weights } = rules;
  const total =
    blockingScore * weights.blocking +
    cdtScore * weights.cdt +
    fullness.sub.score * weights.fullness +
    ergonomics.score * weights.ergonomics;
  const weightSum = weights.blocking + weights.cdt + weights.fullness + weights.ergonomics || 1;

  const gates = evaluateGates(input, stretches, rules);

  return {
    score: Math.round((total / weightSum) * 100),
    weights,
    blocking: {
      score: blockingScore,
      detail: {
        vertical: +vertical.sub.score.toFixed(3),
        horizontal: +horizontal.score.toFixed(3),
      },
      vertical: vertical.sub,
      horizontal,
    },
    cdt: {
      score: cdtScore,
      // The detector key is absent, not zero, when the detector did not run.
      detail: {
        ...(detector ? { detector: +detector.score.toFixed(3) } : {}),
        ordering: +ordering.sub.score.toFixed(3),
      },
      detector,
      ordering: ordering.sub,
    },
    fullness: fullness.sub,
    ergonomics,
    gates,
    gatesPassed: gates.every((gate) => !gate.evaluated || gate.passed),
    worst: {
      blocks: vertical.worst,
      emptiestShelf: fullness.emptiest,
      ordering: ordering.breaks.sort((a, b) => b.violations - a.violations)[0] ?? null,
    },
    rules,
  };
};
